/**
 * =============================================================================
 * particles.js — SISTEM PARTIKEL (DEBU + CONFETTI)
 * =============================================================================
 * dust     → efek debu di belakang semut saat lari (koordinat dunia)
 * confetti → ledakan kemenangan (koordinat layar)
 * =============================================================================
 */

import { rand, randInt } from './config.js';

export class ParticleSystem {
  constructor() {
    this.dust = [];
    this.confetti = [];
    this.maxDust = 400; /* Batas agar tetap 60 FPS saat 100 semut */
  }

  /**
   * spawnDust(x, y, count, color)
   * Munculkan partikel debu di posisi dunia (biasanya di kaki semut).
   */
  spawnDust(x, y, count = 2, color = 'rgba(120,90,50,0.45)') {
    for (let i = 0; i < count; i++) {
      if (this.dust.length >= this.maxDust) this.dust.shift();
      this.dust.push({
        x: x + rand(-6, 2),
        y: y + rand(-4, 4),
        vx: rand(-40, -10),
        vy: rand(-25, 10),
        life: rand(0.25, 0.55),
        age: 0,
        size: rand(1.5, 4),
        color,
      });
    }
  }

  /**
   * burstConfetti(cx, cy, n)
   * Ledakan confetti di koordinat layar (biasanya tengah viewport).
   */
  burstConfetti(cx, cy, n = 120) {
    const colors = ['#ff8c42', '#ffd166', '#06d6a0', '#4cc9f0', '#ef476f', '#fff', '#c77dff'];
    for (let i = 0; i < n; i++) {
      const ang = rand(0, Math.PI * 2);
      const sp = rand(80, 420);
      this.confetti.push({
        x: cx,
        y: cy,
        vx: Math.cos(ang) * sp,
        vy: Math.sin(ang) * sp - rand(80, 200),
        life: rand(1.5, 3.2),
        age: 0,
        size: rand(4, 9),
        rot: rand(0, Math.PI * 2),
        vr: rand(-8, 8),
        color: colors[randInt(0, colors.length - 1)],
        g: rand(280, 480), /* gravitasi per partikel */
      });
    }
  }

  /**
   * update(dt)
   * Gerakkan semua partikel; hapus yang umurnya habis.
   */
  update(dt) {
    /* --- Debu --- */
    for (let i = this.dust.length - 1; i >= 0; i--) {
      const p = this.dust[i];
      p.age += dt;
      if (p.age >= p.life) {
        this.dust.splice(i, 1);
        continue;
      }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vy += 30 * dt;
    }

    /* --- Confetti --- */
    for (let i = this.confetti.length - 1; i >= 0; i--) {
      const p = this.confetti[i];
      p.age += dt;
      if (p.age >= p.life) {
        this.confetti.splice(i, 1);
        continue;
      }
      p.vy += p.g * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vx *= 0.99;
      p.rot += p.vr * dt;
    }
  }

  /**
   * drawDust(ctx, camX, viewW)
   * Gambar debu di world-space (kamera sudah di-apply di race.js).
   * Hanya partikel di dalam viewport yang digambar (culling).
   */
  drawDust(ctx, camX, viewW) {
    const left = camX - 40;
    const right = camX + viewW + 40;
    for (const p of this.dust) {
      if (p.x < left || p.x > right) continue;
      const a = 1 - p.age / p.life;
      ctx.globalAlpha = a * 0.7;
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  }

  /**
   * drawConfetti(ctx)
   * Gambar confetti di screen-space (setelah restore kamera).
   */
  drawConfetti(ctx) {
    for (const p of this.confetti) {
      const a = 1 - p.age / p.life;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.globalAlpha = a;
      ctx.fillStyle = p.color;
      ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
      ctx.restore();
    }
    ctx.globalAlpha = 1;
  }

  /**
   * clear()
   * Kosongkan semua partikel (dipakai saat setup race baru).
   */
  clear() {
    this.dust.length = 0;
    this.confetti.length = 0;
  }
}
